import { AdditiveCapabilities } from '@core/access-control/additive-capabilities';
import { ChildPermissionValue, PermissionsDTO, RootPermissionValue } from '@core/dto/permissions.dto';
import { pipe } from '@core/utils';
import { Model, Serializer } from '@junte/serialize-ts';
import { flatten } from 'flat';

import { BaseModel } from './base-model';

export type InvestorSections = 'administration' | 'emissionProducts';
export type EmittentSections = InvestorSections | 'newProducts';

export type InvestorProductInEmissionSection =
  | 'generalInformation'
  | 'dataRoom'
  | 'nda'
  | 'docs'
  | 'indicativeOffer'
  | 'qa'
  | 'bindingOffer';
export type EmittentProductInEmissionSection = InvestorProductInEmissionSection | 'screening';

type ProductDraftSection = 'generalInformation' | 'docsAndDataRoom' | 'screening' | 'emissionStart';
type AdministrationSection = 'companies' | 'users';

export type Sections = InvestorSections | EmittentSections;
export type Subsections =
  | InvestorProductInEmissionSection
  | EmittentProductInEmissionSection
  | ProductDraftSection
  | AdministrationSection;

export type PrivilegeResource = [Sections, Subsections, ...string[]] | string[] | string;

type FlatPermissions = Record<string, ChildPermissionValue>;
type FlatPermissionsNoInherit = Record<string, RootPermissionValue>;
type PrivilegesObject = Record<string, AdditiveCapabilities>;

const delimiter = '.';

export class PrivilegesSerializer implements Serializer<Privileges> {
  public deserialize(dto: PermissionsDTO): Privileges {
    return pipe<Privileges>(
      flatten,
      removeValueSectionFromKeyNames,
      propagateLevels,
      buildCapabilites,
      removeCompanySectionFromKeyNames,
      buildPrivileges,
    )(dto);
  }

  // just to conform to the interface
  public serialize(): PermissionsDTO {
    return null;
  }
}

@Model(new PrivilegesSerializer())
export class Privileges extends BaseModel {
  private privileges: PrivilegesObject = {};

  public getCapabilities(resource: PrivilegeResource): AdditiveCapabilities {
    if (Array.isArray(resource)) {
      return this.privileges[resource.join(delimiter)];
    }
    return this.privileges[resource];
  }

  public setCapabilities(resource: PrivilegeResource, capabilities: AdditiveCapabilities): void {
    if (Array.isArray(resource)) {
      this.privileges[resource.join(delimiter)] = capabilities;
    } else {
      this.privileges[resource] = capabilities;
    }
  }
}

function buildCapabilites(obj: FlatPermissionsNoInherit): PrivilegesObject {
  const entries = Object.entries(obj);

  const m = new Map<RootPermissionValue, AdditiveCapabilities>([
    [-1, new AdditiveCapabilities()],
    [1, AdditiveCapabilities.create('read')],
    [2, AdditiveCapabilities.create('communicate')],
    [3, AdditiveCapabilities.create('manage')],
  ]);

  const mappedEntries = entries.map(([k, v]) => {
    return [k, m.get(v)];
  });

  return Object.fromEntries(mappedEntries);
}

function buildPrivileges(obj: PrivilegesObject): Privileges {
  return Object.entries(obj).reduce((privileges, [resource, capabilities]) => {
    privileges.setCapabilities(resource, capabilities);
    return privileges;
  }, new Privileges());
}

function propagateLevels(obj: FlatPermissions): FlatPermissionsNoInherit {
  const entries = Object.entries(obj);

  const getNonZeroValue = (key: string): RootPermissionValue | null => {
    if (!key) {
      return null;
    }

    const v = obj[key];

    // retrieve value from parent
    if (v === 0) {
      return getNonZeroValue(`${key.slice(0, key.lastIndexOf('.'))}`);
    }

    return v;
  };

  const mappedEntries = entries.map(([k]) => [k, getNonZeroValue(k)]);

  return Object.fromEntries(mappedEntries);
}

function removeCompanySectionFromKeyNames(obj: PrivilegesObject): PrivilegesObject {
  const entries = Object.entries(obj);

  const mappedEntries = entries.map(([k, v]) => {
    const segments = k.split(delimiter);

    // Backend uses from 2 to 4 components to define privilege key. 4-component key contains: section, subsection,
    // company ID and product ID. As UUIDs are used for IDs it is safe to remove company ID. And removing company ID
    // simplifies enforcing privileges across the app dramatically.
    if (segments.length === 4) {
      return [[segments[0], segments[1], segments[3]].join(delimiter), v];
    }

    return [k, v];
  });

  return Object.fromEntries(mappedEntries);
}

function removeValueSectionFromKeyNames(obj: FlatPermissions): FlatPermissions {
  const entries = Object.entries(obj);
  const mappedEntries = entries.map(([k, v]) => [`${k.slice(0, k.lastIndexOf('.value'))}`, v]);

  return Object.fromEntries(mappedEntries);
}
